import React, { useEffect, useState, useCallback } from 'react';
import { api, SymbolStatus } from '../api';
import { useBackfill } from '../BackfillContext';
import { useAuth } from '../AuthContext';

export default function ManageStocks() {
  const { user, updateStocks } = useAuth();
  const [symbolStatus, setSymbolStatus] = useState<SymbolStatus[]>([]);
  const [newSymbol, setNewSymbol] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [pausingSymbol, setPausingSymbol] = useState<string | null>(null);

  const {
    backfillSymbol, queue, isRunning,
    completedCount, errorCount, progress,
    startBackfill, cancel, dismiss, retryFailed, removeStep,
  } = useBackfill();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const status = await api.getSymbolsStatus();
      setSymbolStatus(status);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const sym = newSymbol.trim().toUpperCase();
    if (!sym) return;
    setAdding(true);
    setError('');
    try {
      const res = await api.addSymbol(sym);
      // Re-load full status (addSymbol returns string[] only)
      const status = await api.getSymbolsStatus();
      setSymbolStatus(status);
      setNewSymbol('');
      // Also add to user's personal stock list so it appears in pages
      if (user && !(user.stocks || []).includes(sym)) {
        await updateStocks([...(user.stocks || []), sym]).catch(() => {});
      }
      startBackfill(sym);
      void res;
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (sym: string) => {
    if (!window.confirm(`Remove ${sym} from tracked stocks? This won't delete existing data.`)) return;
    setError('');
    try {
      await api.removeSymbol(sym);
      setSymbolStatus(prev => prev.filter(s => s.symbol !== sym));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleTogglePause = async (sym: string, currentlyPaused: boolean) => {
    const next = !currentlyPaused;
    setPausingSymbol(sym);
    setError('');
    try {
      const res = await api.setSymbolPaused(sym, next);
      setSymbolStatus(res.status);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPausingSymbol(null);
    }
  };

  if (loading) return <div className="loading">Loading symbols...</div>;

  return (
    <div>
      <h2>Manage Stocks</h2>

      {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Current symbols */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3>Tracked Symbols</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 12px' }}>
          Pause a stock to stop daily data collection (news, quotes, trends, predictions). Existing data is preserved.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {symbolStatus.map(({ symbol: sym, paused }) => {
            const isUpdating = pausingSymbol === sym;
            return (
              <div key={sym} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: paused ? 'rgba(255,165,0,0.08)' : 'var(--bg)',
                padding: '6px 10px 6px 12px', borderRadius: 8,
                border: `1px solid ${paused ? 'var(--yellow, #d49b00)' : 'var(--border)'}`,
                opacity: isUpdating ? 0.5 : 1,
              }}>
                <span style={{
                  fontWeight: 600, fontSize: 14,
                  textDecoration: paused ? 'line-through' : 'none',
                  color: paused ? 'var(--text-muted)' : 'var(--text)',
                }}>{sym}</span>
                {paused && (
                  <span style={{ fontSize: 10, color: 'var(--yellow, #d49b00)', fontWeight: 600 }}>
                    PAUSED
                  </span>
                )}
                <button
                  onClick={() => handleTogglePause(sym, paused)}
                  disabled={isRunning || isUpdating}
                  style={{
                    background: 'none', border: 'none',
                    color: paused ? 'var(--green)' : 'var(--text-muted)',
                    cursor: (isRunning || isUpdating) ? 'not-allowed' : 'pointer',
                    fontSize: 12, lineHeight: 1, padding: '0 4px',
                  }}
                  title={paused ? `Resume data collection for ${sym}` : `Pause data collection for ${sym}`}
                >
                  {paused ? '▶' : '⏸'}
                </button>
                <button
                  onClick={() => handleRemove(sym)}
                  disabled={isRunning || isUpdating}
                  style={{
                    background: 'none', border: 'none', color: 'var(--red)',
                    cursor: (isRunning || isUpdating) ? 'not-allowed' : 'pointer',
                    fontSize: 16, lineHeight: 1, padding: '0 2px',
                  }}
                  title={`Remove ${sym}`}
                >×</button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add new symbol */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3>Add New Stock</h3>
        <p style={{ fontSize: 13, marginBottom: 12 }}>
          Adding a stock will update the environment and start collecting 30 days of historical data
          (newest first). Each step runs sequentially to avoid timeouts. Safe to refresh — progress is saved.
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input
            type="text"
            value={newSymbol}
            onChange={e => setNewSymbol(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="e.g. TSLA"
            disabled={adding || isRunning}
            style={{
              padding: '8px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg)',
              color: 'var(--text)', fontSize: 14, width: 140,
            }}
          />
          <button
            onClick={handleAdd}
            disabled={adding || isRunning || !newSymbol.trim()}
            className="controls"
            style={{
              padding: '8px 20px', borderRadius: 8,
              background: 'var(--accent)', border: '1px solid var(--accent)',
              color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              opacity: (adding || isRunning || !newSymbol.trim()) ? 0.5 : 1,
            }}
          >
            {adding ? 'Adding...' : 'Add Stock'}
          </button>
        </div>
      </div>

      {/* Backfill Queue */}
      {queue.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3>Backfill: {backfillSymbol}</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {isRunning && (
                <button
                  onClick={cancel}
                  style={{
                    padding: '4px 12px', borderRadius: 6,
                    background: 'var(--red)', border: 'none',
                    color: '#fff', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              )}
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {completedCount}/{queue.length} done
                {errorCount > 0 && <span style={{ color: 'var(--red)' }}> ({errorCount} failed)</span>}
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{
            width: '100%', height: 6, background: 'var(--border)',
            borderRadius: 3, marginBottom: 16, overflow: 'hidden',
          }}>
            <div style={{
              width: `${progress}%`, height: '100%',
              background: errorCount > 0 ? 'var(--yellow)' : 'var(--green)',
              borderRadius: 3, transition: 'width 0.3s',
            }} />
          </div>

          {/* Queue list */}
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {queue.map(step => (
              <div key={step.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 0', borderBottom: '1px solid var(--border)',
                fontSize: 13,
              }}>
                <span style={{ width: 20, textAlign: 'center' }}>
                  {step.status === 'pending' && <span style={{ color: 'var(--text-muted)' }}>○</span>}
                  {step.status === 'running' && <span style={{ color: 'var(--accent)' }}>⟳</span>}
                  {step.status === 'success' && <span style={{ color: 'var(--green)' }}>✓</span>}
                  {step.status === 'error' && <span style={{ color: 'var(--red)' }}>✗</span>}
                </span>
                <span style={{
                  color: step.status === 'running' ? 'var(--accent)'
                    : step.status === 'success' ? 'var(--green)'
                    : step.status === 'error' ? 'var(--red)'
                    : 'var(--text-muted)',
                  fontWeight: step.status === 'running' ? 600 : 400,
                }}>
                  {step.label}
                </span>
                {step.error && (
                  <span
                    title={step.error}
                    style={{
                      color: 'var(--red)', fontSize: 11, marginLeft: 'auto',
                      maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap', cursor: 'pointer',
                    }}
                    onClick={e => {
                      const el = e.currentTarget;
                      if (el.style.whiteSpace === 'nowrap') {
                        el.style.whiteSpace = 'normal';
                        el.style.overflow = 'visible';
                      } else {
                        el.style.whiteSpace = 'nowrap';
                        el.style.overflow = 'hidden';
                      }
                    }}
                  >
                    {step.error}
                  </span>
                )}
                {step.status === 'pending' && (step.id.startsWith('news-') || step.id.startsWith('quotes-')) && (
                  <button
                    onClick={() => removeStep(step.id)}
                    style={{
                      marginLeft: 'auto', background: 'none', border: 'none',
                      color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14,
                      lineHeight: 1, padding: '0 4px', opacity: 0.6,
                    }}
                    title={`Remove ${step.label} from queue`}
                  >×</button>
                )}
              </div>
            ))}
          </div>

          {!isRunning && queue.some(s => s.status === 'success' || s.status === 'error') && (
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <button
                onClick={dismiss}
                style={{
                  padding: '6px 16px', borderRadius: 6,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  color: 'var(--text)', fontSize: 13, cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
              {errorCount > 0 && (
                <button
                  onClick={retryFailed}
                  style={{
                    padding: '6px 16px', borderRadius: 6,
                    background: 'var(--accent)', border: '1px solid var(--accent)',
                    color: '#fff', fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Retry Failed
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
